/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useContext } from 'react';
import { get } from 'lodash';
import { FulfillmentOption } from './FulfillmentOption/FulfillmentOption';
import {
  FailedDecision,
  InvalidConsideredOption,
  isValidOption,
  isFailedDecision,
  Option,
  SuccessfulDecision,
  RoutingReceipt,
  RoutingReceiptNodeData,
  OptionNodeValidity,
  IDPResult,
} from '../types';
import { wrapper } from '../sharedStyles';
import { useDecorateOptions } from '../hooks/useDecorateOptions';
import { AccessContext } from './DecisionViewer';
import { NoOptions } from './NoData/NoOptions';
import { Text } from '@cimpress-ui/react';

const scrollBoxWrapperStyles = css`
  padding-left: 1.6rem;
  padding-right: 1.6rem;
  overflow-y: auto;
`;

export const FulfillmentOptions = ({
  routingDecision,
  orderedSkuCode,
  routingRequestUrl,
  isItemSuspendedInRouting,
  routingReceipts,
  idpResults,
  halfWeight,
}: {
  routingDecision: SuccessfulDecision | FailedDecision | undefined;
  orderedSkuCode: string | undefined;
  routingRequestUrl: string | undefined;
  isItemSuspendedInRouting: boolean;
  routingReceipts: RoutingReceipt[] | undefined;
  idpResults: IDPResult[] | undefined;
  halfWeight?: boolean;
}) => {
  const { accessToken, environment } = useContext(AccessContext);

  const fulfillmentOptionWrapper = css`
    padding: ${halfWeight ? '1rem 0 0' : '1.6rem 0 0'};

    > h3 {
      padding: 0 1.6rem;
    }

    .scrollBox {
      box-sizing: border-box;
      list-style-type: none;
      margin-left: 0;
      padding-left: 0;
    }
  `;

  const decoratedOptions = useDecorateOptions({
    environment,
    accessToken,
    decision: routingDecision,
    options: routingDecision?.consideredOptions,
  });

  if (!decoratedOptions || !decoratedOptions.length || decoratedOptions.length === 0) {
    const routingProblem =
      routingDecision && isFailedDecision(routingDecision) ? get(routingDecision, ['problem']) : undefined;

    return (
      <div data-testid="decision-viewer-body-with-errors">
        <NoOptions
          routingProblem={routingProblem}
          options={decoratedOptions}
          orderedSkuCode={orderedSkuCode}
          routingRequestUrl={routingRequestUrl}
          halfWeight={halfWeight}
        />
      </div>
    );
  }

  if (decoratedOptions.some((option) => !option.isFetched)) {
    return null;
  }

  const optionData = decoratedOptions.reduce(
    (acc, option) => {
      if (!option.error && option.data) {
        acc.push(option.data);
      }

      return acc;
    },
    [] as (Option | InvalidConsideredOption)[],
  );

  const nodeData: RoutingReceiptNodeData[] = [];
  const optionNodeValidityDictionary: { [optionId: string]: OptionNodeValidity[] } = optionData.reduce(
    (optionNodeDict, o) => {
      optionNodeDict[o.optionId] = [];
      return optionNodeDict;
    },
    {},
  );

  let currentNodeId: string | undefined;
  let actionTriggered: boolean = false;
  let lastNodeId: string;
  let currentIfThenIndex = 0;
  let passedIfThens: { ifThenId: string; ifThenName?: string }[] = [];

  // Get the last node id so we can mark it as such
  if (routingReceipts && routingReceipts.length) {
    const lastEntry = routingReceipts[routingReceipts.length - 1];
    if ('nodeId' in lastEntry) {
      lastNodeId = lastEntry.nodeId;
    }
  }

  // Associate the option states with each node
  routingReceipts?.forEach((rr) => {
    if ('nodeId' in rr) {
      if (rr.type === 'begin') {
        currentNodeId = rr.nodeId;
        nodeData.push({
          nodeId: rr.nodeId,
          name: rr.name,
          isFinal: rr.nodeId === lastNodeId,
          isStart: !!rr.optionStates,
          optionStates: rr.optionStates,
        });
        currentIfThenIndex = 0;
        passedIfThens = [];
      } else {
        // node end means we're either done or we're moving to a new node
        currentNodeId = undefined;

        // if no actions were triggered by this node then all option states are the same
        // our display logic will take care of the "true" statement if the option is already
        // invalid because that value only represents what the action did, not what
        // the state of the option was before the action triggered
        if (!actionTriggered) {
          optionData.forEach((o) => {
            optionNodeValidityDictionary[o.optionId].push({ nodeId: rr.nodeId, isValid: true });
          });
        }
        actionTriggered = false;
      }
    } else if ('ifThenId' in rr) {
      if (rr.success) {
        passedIfThens.push({
          ifThenId: rr.ifThenId,
          ifThenName: rr.ifThenName,
        });
      }
    } else if ('actionId' in rr && rr.action === 'sort') {
      rr.optionStates.forEach((o) => {
        const optionValidityEntry: OptionNodeValidity = {
          nodeId: currentNodeId!,
          isValid: o.isValid,
          rank: o.rank,
        };

        if (passedIfThens.length) {
          if (passedIfThens.length > currentIfThenIndex) {
            optionValidityEntry.ifThenId = passedIfThens[currentIfThenIndex].ifThenId;
            optionValidityEntry.ifThenName = passedIfThens[currentIfThenIndex].ifThenName;
            currentIfThenIndex++;
          } else {
            optionValidityEntry.isNodeAction = true;
          }
        }

        optionNodeValidityDictionary[o.optionId].push(optionValidityEntry);
      });
      actionTriggered = true;
    } else if ('actionId' in rr && (rr.action === 'filter' || rr.action === 'sortAndFilter')) {
      rr.optionStates.forEach((o) => {
        const optionValidityEntry: OptionNodeValidity = {
          nodeId: currentNodeId!,
          isValid: o.isValid,
          cause: rr.cause,
          rank: o.rank,
        };

        if (passedIfThens.length) {
          if (passedIfThens.length > currentIfThenIndex) {
            optionValidityEntry.ifThenId = passedIfThens[currentIfThenIndex].ifThenId;
            optionValidityEntry.ifThenName = passedIfThens[currentIfThenIndex].ifThenName;
            currentIfThenIndex++;
          } else {
            optionValidityEntry.isNodeAction = true;
          }
        }

        optionNodeValidityDictionary[o.optionId].push(optionValidityEntry);
      });
      actionTriggered = true;
    }
  });

  let optionDataWithRoutingReceiptInfo: (Option | InvalidConsideredOption)[] | undefined;
  if (routingReceipts && routingReceipts.length) {
    optionDataWithRoutingReceiptInfo = optionData.map((o) => ({
      ...o,
      routingReceiptData: {
        nodeData,
        optionNodeValidity: optionNodeValidityDictionary[o.optionId],
      },
    }));
  }

  let optionDataWithIDPResultInfo: (Option | InvalidConsideredOption)[] | undefined;
  if (idpResults && idpResults.length) {
    optionDataWithIDPResultInfo = (optionDataWithRoutingReceiptInfo || optionData).map((o) => {
      const matchingIDPResult = idpResults.find((idp) => idp.optionId === o.optionId);
      return {
        ...o,
        idpResult: matchingIDPResult?.result,
      };
    });
  }

  const optionsToUse = optionDataWithIDPResultInfo || optionDataWithRoutingReceiptInfo || optionData;

  const sortedOptions = optionsToUse.sort((a, b) => {
    if (isValidOption(a) && a.rank && isValidOption(b) && b.rank) {
      return Number(a.rank) - Number(b.rank);
    } else if (isValidOption(a) && a.rank) {
      return -1;
    } else if (isValidOption(b) && b.rank) {
      return 1;
    } else {
      // Sort by alphanumeric SKU if we don't have a rank to sort by (i.e., invalid options)
      return a.referenceId.localeCompare(b.referenceId, 'en', {
        numeric: true,
        sensitivity: 'base',
      });
    }
  });

  return (
    <div css={[wrapper, fulfillmentOptionWrapper]} data-testid="fulfillment-options">
      <Text as="h3" UNSAFE_className={halfWeight ? 'halfWeight' : ''}>
        Fulfillment Options
      </Text>
      <div css={scrollBoxWrapperStyles}>
        <ul className="scrollBox">
          {sortedOptions.map((option) => (
            <li key={`${option.optionId}-list-item`} data-testid="option-list-item">
              <FulfillmentOption
                option={option}
                isItemSuspendedInRouting={isItemSuspendedInRouting}
                halfWeight={halfWeight}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
